/**
 * MARKETPLACE ITEM CARD
 * 
 * A read-only card for browsing items from other users.
 * Features:
 * - Photo display with watch (star) toggle
 * - Item type badge (Polish/Plate/Tool/etc.) - Top left
 * - Status badge (Available/Pending/Sold) - Below item type
 * - Swap badge - Top right (if applicable)
 * - Item info (name, brand)
 * - Condition badge (New/Swatched/Used)
 * - Quantity indicator (if listing more than 1)
 * - Time listed indicator
 * - Seller info with favorite heart toggle
 * - "Copy to My Collection" button
 * - "Message Seller" button
 * 
 * UPDATED: Item type badge at top-left, status badge below it
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, getDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Copy, Tag, Repeat, Check, Loader2, ExternalLink, MessageSquare, Heart, Clock, Package, Sparkles, Star, MapPin } from 'lucide-react';
import { AppIcons } from '../../constants/icons';
import { getCategoryColors } from '../../constants/categoryColors';
import { formatPrice } from '../../constants/currencies';
import InquiryModal from '../../messaging/components/InquiryModal';
import type { CollectionItem } from '../../types';
import type { AttachedItem } from '../../messaging/types';

interface MarketplaceItem extends CollectionItem {
  sellerId: string;
  sellerUsername?: string;
}

interface MarketplaceItemCardProps {
  item: MarketplaceItem;
  currentUserId: string | null;
  onCopySuccess?: () => void;
  onClick?: () => void;
  isWatching?: boolean;
  onWatchChange?: (isWatching: boolean) => void;
}

const MarketplaceItemCard: React.FC<MarketplaceItemCardProps> = ({
  item,
  currentUserId,
  onCopySuccess,
  onClick,
  isWatching: externalIsWatching,
  onWatchChange
}) => {
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDetails, _setShowDetails] = useState(false);
  const [sellerUsername, setSellerUsername] = useState<string | null>(null);
  const [sellerAvatar, setSellerAvatar] = useState<string | null>(null);
  const [sellerLocation, setSellerLocation] = useState<string | null>(null);
  const [sellerShipsTo, setSellerShipsTo] = useState<string | null>(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  
  // Watching state (star icon) - use external prop if provided
  const [internalIsWatching, setInternalIsWatching] = useState(false);
  const isWatching = externalIsWatching !== undefined ? externalIsWatching : internalIsWatching;
  const [watchingLoading, setWatchingLoading] = useState(false);
  
  // Favorite seller state (heart icon)
  const [isSellerFavorited, setIsSellerFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  
  // Login prompt state
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Fetch seller's username and avatar
  useEffect(() => {
    const fetchSellerInfo = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', item.sellerId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSellerUsername(userData.username || userData.profile?.username || null);
          setSellerAvatar(userData.avatarUrl || userData.profile?.avatarUrl || null);
          setSellerLocation(userData.location || userData.profile?.location || null);
          setSellerShipsTo(userData.shipsTo || userData.profile?.shipsTo || null);
        }
      } catch (err) {
        console.error('Error fetching seller info:', err);
      }
    };

    if (item.sellerId) {
      fetchSellerInfo();
    }
  }, [item.sellerId]);

  // Check if item is being watched (only if not controlled externally)
  useEffect(() => {
    // Skip if externally controlled
    if (externalIsWatching !== undefined) return;
    
    const checkWatching = async () => {
      if (!currentUserId) return;
      
      try {
        const watchingRef = collection(db, 'users', currentUserId, 'marketplaceWishlist');
        const q = await getDocs(watchingRef);
        const isOnList = q.docs.some(doc => doc.data().itemId === item.id && doc.data().sellerId === item.sellerId);
        setInternalIsWatching(isOnList);
      } catch (err) {
        console.error('Error checking watching status:', err);
      }
    };

    checkWatching();
  }, [currentUserId, item.id, item.sellerId, externalIsWatching]);

  // Check if seller is favorited
  useEffect(() => {
    const checkFavorite = async () => {
      if (!currentUserId || !item.sellerId) return;
      
      try {
        const favoritesRef = collection(db, 'users', currentUserId, 'favoriteSellers');
        const q = await getDocs(favoritesRef);
        const isFavorited = q.docs.some(doc => doc.data().sellerId === item.sellerId);
        setIsSellerFavorited(isFavorited);
      } catch (err) {
        console.error('Error checking favorite status:', err);
      }
    };

    checkFavorite();
  }, [currentUserId, item.sellerId]);

  // Toggle watching
  const handleWatchToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }

    setWatchingLoading(true);
    try {
      const watchingRef = collection(db, 'users', currentUserId, 'marketplaceWishlist');
      
      if (isWatching) {
        // Remove from watching
        const q = await getDocs(watchingRef);
        const docToDelete = q.docs.find(doc => doc.data().itemId === item.id && doc.data().sellerId === item.sellerId);
        if (docToDelete) {
          await deleteDoc(doc(db, 'users', currentUserId, 'marketplaceWishlist', docToDelete.id));
        }
        setInternalIsWatching(false);
        onWatchChange?.(false);
      } else {
        // Add to watching
        await addDoc(watchingRef, {
          itemId: item.id,
          sellerId: item.sellerId,
          itemName: item.name,
          itemBrand: item.brand,
          itemType: item.itemType,
          photoUrl: getDisplayImage() || null,
          price: item.destashPrice || null,
          addedAt: serverTimestamp()
        });
        setInternalIsWatching(true);
        onWatchChange?.(true);
      }
    } catch (err) {
      console.error('Error toggling watch status:', err);
    } finally {
      setWatchingLoading(false);
    }
  };

  // Toggle favorite seller
  const handleFavoriteToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }

    if (item.sellerId === currentUserId) {
      return; // Can't favorite yourself
    }

    setFavoriteLoading(true);
    try {
      const favoritesRef = collection(db, 'users', currentUserId, 'favoriteSellers');
      
      if (isSellerFavorited) {
        // Remove from favorites
        const q = await getDocs(favoritesRef);
        const docToDelete = q.docs.find(doc => doc.data().sellerId === item.sellerId);
        if (docToDelete) {
          await deleteDoc(doc(db, 'users', currentUserId, 'favoriteSellers', docToDelete.id));
        }
        setIsSellerFavorited(false);
      } else {
        // Add to favorites
        await addDoc(favoritesRef, {
          sellerId: item.sellerId,
          sellerUsername: sellerUsername || null,
          sellerAvatar: sellerAvatar || null,
          addedAt: serverTimestamp()
        });
        setIsSellerFavorited(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Calculate time since listed
  const getTimeAgo = (): string | null => {
    const listedDate = item.destashDate || item.updatedAt || item.createdAt;
    if (!listedDate) return null;

    const date = (listedDate as any).toDate ? (listedDate as any).toDate() : new Date(listedDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Listed today';
    if (diffDays === 1) return 'Listed yesterday';
    if (diffDays < 7) return `Listed ${diffDays} days ago`;
    if (diffDays < 30) return `Listed ${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `Listed ${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `Listed ${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  };

  // Get display image
  const getDisplayImage = (): string | null => {
    if (item.photos && item.photos.length > 0) {
      const defaultPhoto = item.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : item.photos[0].url;
    }
    if (item.swatchPhotos && item.swatchPhotos.length > 0) return item.swatchPhotos[0];
    if (item.bottlePhotos && item.bottlePhotos.length > 0) return item.bottlePhotos[0];
    if (item.photoUrls && item.photoUrls.length > 0) return item.photoUrls[0];
    return null;
  };

  const displayImage = getDisplayImage();

  // Get item type badge (color and label)
  const getItemTypeBadge = () => {
    const colors = getCategoryColors(item.itemType);
    switch (item.itemType) {
      case 'polish': return { bg: colors.bg, label: 'Polish', icon: AppIcons.Polish };
      case 'plate': return { bg: colors.bg, label: 'Plate', icon: AppIcons.Plate };
      case 'embellishment': return { bg: colors.bg, label: 'Embellishment', icon: AppIcons.Embellishment };
      case 'tool': return { bg: colors.bg, label: 'Tool', icon: AppIcons.Tool };
      case 'transfer': return { bg: colors.bg, label: 'Decal', icon: AppIcons.Decal };
      case 'powder': return { bg: colors.bg, label: 'Powder', icon: AppIcons.Powder };
      case 'acrylic': return { bg: colors.bg, label: 'Acrylic', icon: AppIcons.Embellishment };
      default: return { bg: 'bg-gray-500', label: 'Item', icon: AppIcons.Creation };
    }
  };

  // Get status badge styling
  const getStatusBadge = () => {
    const status = item.destashStatus || 'available';
    switch (status) {
      case 'available':
        return { bg: 'bg-green-500', text: 'Available' };
      case 'pending':
        return { bg: 'bg-yellow-500', text: 'Pending' };
      case 'sold':
        return { bg: 'bg-red-500', text: 'Sold' };
      default:
        return { bg: 'bg-green-500', text: 'Available' };
    }
  };

  const statusBadge = getStatusBadge();
  const itemTypeBadge = getItemTypeBadge();

  // Copy item to current user's collection
  const handleCopyToCollection = async () => {
    if (!currentUserId) {
      alert('Please sign in to add items to your collection');
      return;
    }

    setCopying(true);

    try {
      // Copy photos WITH source tracking for DMCA
      const copiedPhotos = item.photos?.map(photo => ({
        url: photo.url,
        caption: photo.caption || `${item.brand}/${item.name}`,
        isDefault: photo.isDefault || false,
        // DMCA Source Tracking
        uploadedBy: currentUserId,
        uploadedAt: new Date().toISOString(),
        source: 'marketplace' as const,
        sourceUrl: photo.url,
        sourceUserId: item.sellerId,
        dmcaStatus: 'active' as const,
        // Preserve existing credit if present
        ...(photo.creditName && { creditName: photo.creditName }),
        ...(photo.creditUrl && { creditUrl: photo.creditUrl }),
      })) || [];

      const newItemData: any = {
        name: item.name,
        nameLower: (item.name ?? '').toLowerCase(),
        brand: item.brand,
        brandLower: (item.brand ?? '').toLowerCase(),
        itemType: item.itemType,
        colorDescription: item.colorDescription || '',
        tags: item.tags || [],
        photos: copiedPhotos,
        isOwned: false,
        onWishlist: false, // Don't add to wishlist - they're copying because they want it
        // No isoPriority - they're not searching for it, they found it
        dateAdded: serverTimestamp(),
        copiedFrom: {
          itemId: item.id,
          sellerId: item.sellerId,
          sellerUsername: sellerUsername,
        }
      };

      // Add to user's collection
      const itemsRef = collection(db, 'users', currentUserId, 'items');
      await addDoc(itemsRef, newItemData);

      setCopied(true);
      onCopySuccess?.();
      
      // Reset after 3 seconds
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Error copying to collection:', err);
      alert('Failed to copy item. Please try again.');
    } finally {
      setCopying(false);
    }
  };

  // Prepare attached item for inquiry modal
  const getAttachedItem = (): AttachedItem => ({
    itemId: item.id,
    ownerId: item.sellerId,
    name: item.name,
    brand: item.brand,
    photoUrl: displayImage,
    status: item.destashStatus || 'available',
  });

  return (
    <>
      <div 
        className="card-light hover:shadow-2xl transition-all overflow-hidden group cursor-pointer flex flex-col"
        onClick={onClick}
      >
        {/* Image Section */}
        <div className="aspect-square relative overflow-hidden">
          {displayImage ? (
            <img
              src={displayImage}
              alt={`${item.brand} ${item.name}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full rainbow-gradient flex items-center justify-center">
              <AppIcons.Creation className="w-14 h-14 text-white" aria-label="Item" />
            </div>
          )}

          {/* Item Type Badge - Top left */}
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-1 ${itemTypeBadge.bg} text-white rounded-full text-xs font-bold shadow flex items-center gap-1 font-body`}>
              {React.createElement(itemTypeBadge.icon, { className: 'w-3 h-3 text-current', 'aria-label': itemTypeBadge.label })}
              {itemTypeBadge.label}
            </span>
          </div>

          {/* Status Badge - Below item type */}
          <div className="absolute top-10 left-2">
            <span className={`px-2 py-1 ${statusBadge.bg} text-white rounded-full text-xs font-bold shadow font-body`}>
              {statusBadge.text}
            </span>
          </div>

          {/* Swap Badge - Top right - ONLY for items that have BOTH price AND swaps */}
          {item.isOpenToSwaps && (item.destashPrice ?? 0) > 0 && item.destashStatus !== 'sold' && (
            <div className="absolute top-2 right-2">
              <span className="px-2 py-1 bg-blue-500 text-white rounded-full text-xs font-bold flex items-center gap-1 shadow font-body">
                <Repeat className="icon-xs" />
                + Trade
              </span>
            </div>
          )}

          {/* Watch Button (Star icon) - Bottom right */}
          {item.destashStatus !== 'sold' && (
            <button
              onClick={handleWatchToggle}
              disabled={watchingLoading}
              className={`absolute bottom-2 right-2 p-2 rounded-full shadow-lg transition-all ${
                isWatching
                  ? 'bg-amber-500 text-white'
                  : 'bg-white/90 text-light-secondary hover:bg-white hover:text-amber-500'
              }`}
              title={isWatching ? 'Stop watching' : 'Wishlist/Watchlist'}
            >
              {watchingLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Star className={`w-5 h-5 ${isWatching ? 'fill-current' : ''}`} />
              )}
            </button>
          )}

          {/* Sold Overlay */}
          {item.destashStatus === 'sold' && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold text-lg transform -rotate-12 shadow-lg">
                SOLD
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4 flex flex-col flex-1">
          {/* Brand & Name */}
          <div className="mb-2">
            <h3 className="text-xs uppercase text-light-primary font-bold tracking-wide mb-1 font-heading">
              {item.brand}
            </h3>
            <h4 className="text-lg font-bold text-light-primary leading-tight font-body">
              {item.name}
            </h4>
          </div>

          {/* Color Description */}
          {item.colorDescription && (
            <p className="text-sm text-light-secondary mb-2 line-clamp-2 font-body">
              {item.colorDescription}
            </p>
          )}

          {/* Price & Badges Section */}
          {(item.destashPrice || item.isOpenToSwaps || item.destashCondition) && (
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              {/* Price Badge - only show if there's a real price (not 0) */}
              {(item.destashPrice ?? 0) > 0 && (
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold font-body ${item.destashStatus === 'sold' ? 'bg-gray-200 text-light-muted line-through' : 'bg-green-100 text-green-800'}`}>
                  <Tag className="icon-sm" />
                  {formatPrice(item.destashPrice, item.destashCurrency)}
                </span>
              )}
              {/* Ships-to badge (structured field). Worldwide takes precedence over country list. */}
              {item.destashShipsToWorldwide ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-sky-100 text-sky-700 rounded-full text-xs font-medium font-body">
                  <span aria-hidden="true">🌍</span>
                  Worldwide
                </span>
              ) : (item.destashShipsToCountries && item.destashShipsToCountries.length > 0) ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-sky-100 text-sky-700 rounded-full text-xs font-medium font-body">
                  <MapPin className="icon-xs" />
                  {item.destashShipsToCountries.slice(0, 3).join(' · ')}
                  {item.destashShipsToCountries.length > 3 && ` +${item.destashShipsToCountries.length - 3}`}
                </span>
              ) : null}
              {/* Trade Only Badge - inline version for items without price */}
              {item.isOpenToSwaps && (!item.destashPrice || item.destashPrice === 0) && item.destashStatus !== 'sold' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold font-body bg-purple-100 text-purple-700">
                  <Repeat className="icon-sm" />
                  Trade Only
                </span>
              )}
              {/* Condition Badge */}
              {item.destashCondition && (() => {
                // Normalize condition for comparison (handle case variations)
                const condition = item.destashCondition?.toLowerCase();
                
                if (condition === 'new') {
                  return (
                    <span className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-emerald-100 text-emerald-700">
                      <Sparkles className="w-3 h-3 text-current" aria-label="New" />
                      <span className="font-body">New</span>
                    </span>
                  );
                } else if (condition === 'swatched only') {
                  return (
                    <span className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-blue-100 text-blue-700">
                      <AppIcons.Polish className="w-3 h-3 text-current" aria-label="Swatched" />
                      <span className="font-body">Swatched</span>
                    </span>
                  );
                } else if (condition === 'used - light use' || condition === 'used - moderate') {
                  return (
                    <span className="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-amber-100 text-amber-700">
                      <Package className="w-3 h-3 text-current" aria-label="Used" />
                      <span className="font-body">Used</span>
                    </span>
                  );
                }
                // Unknown condition - don't show badge (matches detail modal behavior)
                return null;
              })()}
              {/* Quantity Badge - show destashQuantity or default to 1 */}
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium font-body">
                <Package className="icon-xs" />
                Qty: {item.destashQuantity || 1}
              </span>
            </div>
          )}

          {/* Time Listed */}
          {getTimeAgo() && item.destashStatus !== 'sold' && (
            <div className="mb-2 flex items-center gap-1 text-xs text-light-muted font-body">
              <Clock className="icon-xs" />
              {getTimeAgo()}
            </div>
          )}

          {/* Destash Notes */}
          {item.destashNotes && showDetails && (
            <div className="mb-2 p-2 border border-gray-200 rounded-lg">
              <p className="text-sm text-light-secondary font-body">
                "{item.destashNotes}"
              </p>
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Divider */}
          <div className="border-t border border-gray-300 my-3"></div>

          {/* Seller Info with Favorite Heart */}
          <div className="flex items-center justify-between mb-3">
            {sellerUsername ? (
              <div className="flex items-center gap-2">
                <Link 
                  to={`/profile/${sellerUsername}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2 text-sm text-light-secondary hover:text-light-primary transition-default font-body"
                >
                  {sellerAvatar ? (
                    <img 
                      src={sellerAvatar} 
                      alt={sellerUsername}
                      className="w-6 h-6 rounded-full object-cover border border-gray-300"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full rainbow-gradient flex items-center justify-center text-white text-xs font-bold">
                      {sellerUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span>@{sellerUsername}</span>
                  <ExternalLink className="icon-xs" />
                </Link>
                
                {/* Favorite Seller Heart */}
                {currentUserId && item.sellerId !== currentUserId && (
                  <button
                    onClick={handleFavoriteToggle}
                    disabled={favoriteLoading}
                    className={`p-1 rounded-full transition-all ${
                      isSellerFavorited
                        ? 'text-red-500'
                        : 'text-gray-300 hover:text-red-400'
                    }`}
                    title={isSellerFavorited ? 'Remove from favorites' : 'Add seller to favorites'}
                  >
                    <Heart className={`w-4 h-4 ${isSellerFavorited ? 'fill-current' : ''}`} />
                  </button>
                )}
              </div>
            ) : (
              <span className="flex items-center gap-2 text-sm text-light-muted font-body">
                <div className="w-6 h-6 rounded-full bg-gray-200 animate-pulse"></div>
                <span>Loading seller...</span>
              </span>
            )}
          </div>

          {/* Shipping Info */}
          {(sellerLocation || sellerShipsTo) && (
            <div className="mb-3 flex flex-col gap-1 text-xs text-light-secondary font-body">
              {sellerLocation && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>Ships from {sellerLocation}</span>
                </div>
              )}
              {sellerShipsTo && (
                <div className="flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" />
                  <span>Ships to {sellerShipsTo}</span>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {item.destashStatus !== 'sold' ? (
            <div className="flex gap-2">
              {/* Message Seller Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInquiryModal(true);
                }}
                disabled={!currentUserId || item.sellerId === currentUserId}
                className={`flex-1 py-2 px-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 font-body ${
                  !currentUserId || item.sellerId === currentUserId
                    ? 'bg-gray-200 text-light-muted cursor-not-allowed'
                    : 'bg-white border-2 border-gray-300 text-light-primary hover:bg-gray-100'
                }`}
              >
                <MessageSquare className="icon-sm" />
                Message
              </button>

              {/* Copy to Collection Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyToCollection();
                }}
                disabled={copying || copied || !currentUserId}
                className={`flex-1 py-2 px-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 font-body ${
                  copied
                    ? 'bg-green-500 text-white'
                    : copying
                      ? 'bg-gray-300 text-light-muted cursor-wait'
                      : !currentUserId
                        ? 'bg-gray-200 text-light-muted cursor-not-allowed'
                        : 'btn-primary'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="icon-sm" />
                    Added!
                  </>
                ) : copying ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Copy className="icon-sm" />
                    Copy
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="text-center py-2 text-light-muted text-sm font-body">
              This item has been sold
            </div>
          )}
          
          {!currentUserId && item.destashStatus !== 'sold' && (
            <p className="text-xs text-light-muted text-center mt-2 font-body">
              Sign in to message or add items
            </p>
          )}
        </div>
      </div>

      {/* Inquiry Modal */}
      {currentUserId && (
        <InquiryModal
          isOpen={showInquiryModal}
          onClose={() => setShowInquiryModal(false)}
          currentUserId={currentUserId}
          sellerId={item.sellerId}
          sellerUsername={sellerUsername}
          items={[getAttachedItem()]}
        />
      )}

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 text-center">
            <div className="flex justify-center mb-4">
              <AppIcons.UserIcon className="w-16 h-16 text-gray-700" aria-label="Sign In" />
            </div>
            <h3 className="text-xl font-bold text-light-primary mb-2 font-heading">
              Sign In Required
            </h3>
            <p className="text-light-secondary mb-4 font-body">
              You need to be signed in to watch items.
            </p>
            <div className="flex gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLoginPrompt(false);
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-light-secondary rounded-lg hover:bg-gray-200 transition-default font-body"
              >
                Cancel
              </button>
              <Link
                to="/auth"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-default text-center font-body"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceItemCard;
